import { useLocalSearchParams, useRouter } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Image,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import Icon from "react-native-vector-icons/FontAwesome";
import socket from "../api/socket_io";
import FooterComponent from "../component/footer";
import { useLanguage } from "./LanguageProvider";

export default function order_scales() {
  const router = useRouter();
  interface ForkliftJob {
    date: string;
    time: string;
    emp_code: string;
    emp_name: string;
    product_id: string;
    meter: string;
    unit: string;
    reel_no: string;
    material_code: string;
    emp_position: string;
    order_name: string;
    order_number: string;
    name: string;
    position: string;
    machine: string;
    type: string;
    status: string;
    container_id: string;
    ncr_type: string;
    qrcode: string;
  }

  const [allData, setAllData] = useState<ForkliftJob[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);

  const [isConnected, setIsConnected] = useState(socket.connected);
  const [networkError, setNetworkError] = useState<string | null>(null);

  const { machine_code, item: itemParam } = useLocalSearchParams();
  const { t } = useLanguage();

  const item2 = useMemo(() => {
    const normalizedItemParam = Array.isArray(itemParam)
      ? itemParam[0]
      : itemParam;

    if (typeof normalizedItemParam !== "string") {
      return normalizedItemParam ?? null;
    }

    try {
      return JSON.parse(normalizedItemParam);
    } catch (error) {
      console.error("ไม่สามารถอ่าน item จาก Route Parameter ได้:", error);
      return null;
    }
  }, [itemParam]);

  const socketEventNamesKey = useMemo(() => {
    const routeMachineCode = Array.isArray(machine_code)
      ? machine_code[0]
      : machine_code;

    const machineCandidates = [
      routeMachineCode,
      item2?.machine_code,
      item2?.machineCode,
      item2?.machine,
    ];

    const normalizedCodes = machineCandidates
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean);

    return Array.from(new Set(normalizedCodes))
      .map((code) => `weight_${code}`)
      .join(",");
  }, [machine_code, item2]);

  const socketEventNames = useMemo(() => {
    return socketEventNamesKey ? socketEventNamesKey.split(",") : [];
  }, [socketEventNamesKey]);

  const data = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();

    if (!keyword) {
      return allData;
    }

    return allData.filter((job) =>
      String(job?.container_id ?? "")
        .toLowerCase()
        .includes(keyword),
    );
  }, [allData, searchQuery]);

  useEffect(() => {
    if (socketEventNames.length === 0) {
      setAllData([]);
      setLoading(false);
      setNetworkError("ไม่พบรหัสเครื่องจักร (machine_code)");
      return;
    }

    let isActive = true;
    let hasReceivedData = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const requestData = () => {
      if (!isActive) return;
      socketEventNames.forEach((eventName) => {
        console.log("Requesting Socket Event:", eventName);
        socket.emit(eventName as any);
      });
    };

    const handleConnect = () => {
      console.log("Connected to socket:", socket.id);
      if (isActive) {
        setIsConnected(true);
        setNetworkError(null);
        requestData();
      }
    };

    const handleData = (incomingData: unknown, receivedEvent: string) => {
      if (!isActive) return;

      hasReceivedData = true; // ทำเครื่องหมายว่าได้ข้อมูลแล้ว
      setIsConnected(true);
      setNetworkError(null);

      // ถ้าได้ข้อมูลแล้ว ให้เคลียร์ Timeout ทิ้งทันที ไม่ต้องรอครบ 15 วิ
      if (timeoutId) clearTimeout(timeoutId); 

      const normalizedData = Array.isArray(incomingData)
        ? incomingData
        : Array.isArray((incomingData as any)?.data)
        ? (incomingData as any).data
        : Array.isArray((incomingData as any)?.result)
        ? (incomingData as any).result
        : null;

      if (!normalizedData) {
        setAllData([]);
      } else {
        console.log(`Received ${normalizedData.length} records from ${receivedEvent}`);
        setAllData(normalizedData as ForkliftJob[]);
      }
      
      setLoading(false); // ปิดหน้าโหลดทันทีที่ได้ข้อมูล
    };

    const eventHandlers = socketEventNames.map((eventName) => {
      const handler = (incomingData: unknown) =>
        handleData(incomingData, eventName);

      socket.on(eventName as any, handler);
      return { eventName, handler };
    });

    const handleConnectError = (socketError: Error) => {
      if (!isActive) return;
      console.error("Socket connection error:", socketError?.message);
      setIsConnected(false);
      setNetworkError("ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ (Network Error)");
      setLoading(false);
    };

    const handleDisconnect = (reason: string) => {
      console.log("Socket disconnected:", reason);
      if (isActive) {
        setIsConnected(false);
        setNetworkError("การเชื่อมต่อสัญญาณขาดหาย กำลังต่อใหม่...");
      }
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.on("disconnect", handleDisconnect);

    if (socket.connected) {
      setIsConnected(true);
      requestData();
    } else {
      setIsConnected(false);
      socket.connect();
    }

    // ⏳ สั่งรอสูงสุด 15 วินาที ถ้าเกินกว่านี้และไม่มีข้อมูล ถือว่าไม่มีรายการ!
    timeoutId = setTimeout(() => {
      if (isActive && !hasReceivedData) {
        console.log("Timeout: 15s passed with no data. Stopping loader.");
        if (socket.connected) {
          setNetworkError(null);
        } else {
          setNetworkError("เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ โปรดเช็คสัญญาณ Wi-Fi");
        }
        setAllData([]);
        setLoading(false); // ⚠️ หยุดหมุนทันที
      }
    }, 15000);

    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);

      eventHandlers.forEach(({ eventName, handler }) => {
        socket.off(eventName as any, handler);
      });

      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("disconnect", handleDisconnect);
    };
  }, [socketEventNamesKey, retryKey]); 

  return (
    <>
      <View className="flex-1 bg-[#F17736] items-center justify-center">
        <View className="w-full h-[20%] bg-[#F17736] rounded-b-[50px] items-center justify-between"></View>
        <View className="bottom-0 w-full h-[90%] bg-white rounded-t-[20px] items-center justify-center"></View>

        <FooterComponent />

        {loading ? (
          <View className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center">
            <ActivityIndicator size="large" color="#F17736" />
            <Text className="text-black text-lg font-kanit mt-4">
              {t("loading_data")}
            </Text>
          </View>
        ) : (
          <>
            <View className="absolute mt-2 top-0 w-[90%] h-[95%] px-2 bg-white rounded-2xl border border-gray-200 shadow-md">
              
              {(!isConnected || networkError) && (
                <View className="w-full bg-red-500 py-2 px-4 rounded-t-xl flex-row items-center justify-center mt-1">
                  <Icon name="wifi" size={16} color="white" />
                  <Text className="text-white font-kanit text-sm ml-2">
                    {networkError || "หลุดการเชื่อมต่อจากเซิร์ฟเวอร์..."}
                  </Text>
                </View>
              )}

              {data.length === 0 ? (
                <View className="flex-1 items-center justify-center">
                  <Image
                    source={require("../../assets/paper.png")}
                    resizeMode="contain"
                    style={{ width: 128, height: 128 }}
                  />
                  <Text className="text-2xl font-kanit text-gray-500 mt-4">
                    {t("no_order")}
                  </Text>
                  <Text className="text-xl font-kanit text-gray-400">
                    {t("no_ordernow")}
                  </Text>

                  <TouchableOpacity
                    onPress={() => {
                      setLoading(true);
                      setRetryKey((current) => current + 1); // 🔄 กดเพื่อสั่งให้เริ่ม 15 วิ ใหม่
                    }}
                    className={`mt-5 px-5 py-2 rounded-lg ${
                      !isConnected ? "bg-red-500" : "bg-[#F17736]"
                    }`}
                  >
                    <Text className="text-white font-kanit">
                      {!isConnected ? "ลองเชื่อมต่อสัญญาณใหม่" : "ลองโหลดรายการใหม่"}
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => {
                      if (router.canGoBack()) {
                        router.back();
                      } else {
                        router.replace("/screens/home");
                      }
                    }}
                    className="bg-[#58d68d] mt-3 px-4 py-2 rounded-lg"
                  >
                    <Text className="text-white font-kanit">
                      {t("back_to_home")}
                    </Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <ScrollView
                  contentContainerStyle={{
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  showsVerticalScrollIndicator={false}
                  className="w-full mt-2"
                >
                  <View className="relative w-full flex flex-row items-center py-2 mb-2">
                    <TextInput
                      placeholder={t("search_container_id")}
                      value={searchQuery}
                      onChangeText={setSearchQuery}
                      className="w-full h-14 border border-gray-300 rounded-lg px-4 pr-12 bg-gray-50 font-kanit text-gray-600"
                    />

                    {searchQuery.length > 0 && (
                      <TouchableOpacity
                        onPress={() => setSearchQuery("")}
                        className="absolute right-3 h-14 flex items-center justify-center"
                      >
                        <Text className="text-white text-md font-kanit bg-red-500 rounded-lg px-3 py-1">
                          {t("delete")}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>

                  {data.map((item, index) => (
                    <TouchableOpacity
                      key={index}
                      className="py-1 px-3 mt-2 mb-4 rounded-xl border-gray-100 border-2 bg-white shadow-sm w-full"
                      onPress={() =>
                        router.push({
                          pathname: "/screens/detailorder",
                          params: {
                            item: JSON.stringify(item),
                            item2: JSON.stringify(item2),
                          },
                        })
                      }
                    >
                      <View className="flex-row items-center justify-between mb-1">
                        <View className="flex-row items-center">
                          <View
                            className={`flex-row items-center justify-center py-0.5 px-1.5 rounded-lg mr-3 min-h-6 ${
                              item.ncr_type === "0"
                                ? item.type === "good"
                                  ? "bg-green-50"
                                  : item.type === "waste"
                                  ? "bg-red-50"
                                  : "bg-amber-50"
                                : "bg-blue-50"
                            }`}
                          >
                            <Icon
                              name="clock-o"
                              size={15}
                              className="pr-1"
                              color={
                                item.ncr_type === "0"
                                  ? item.type === "good"
                                    ? "#58d68d"
                                    : item.type === "waste"
                                    ? "#ec7063"
                                    : "#f4d03f"
                                  : "#60a5fa"
                              }
                            />
                            <Text
                              className={`text-md font-kanit ${
                                item.ncr_type === "0"
                                  ? item.type === "good"
                                    ? "text-green-400"
                                    : item.type === "waste"
                                    ? "text-red-400"
                                    : "text-amber-400"
                                  : "text-blue-400"
                              }`}
                            >
                              {item.time ? item.time + " น." : t("no_time")}
                            </Text>
                          </View>

                          <View
                            className={`flex-row items-center justify-center py-0.5 px-1.5 rounded-lg mr-3 min-h-6 ${
                              item.ncr_type === "0"
                                ? item.type === "good"
                                  ? "bg-green-50"
                                  : item.type === "waste"
                                  ? "bg-red-50"
                                  : "bg-amber-50"
                                : "bg-blue-50"
                            }`}
                          >
                            <Icon
                              name="calendar"
                              size={12}
                              className="pr-1"
                              color={
                                item.ncr_type === "0"
                                  ? item.type === "good"
                                    ? "#58d68d"
                                    : item.type === "waste"
                                    ? "#ec7063"
                                    : "#f4d03f"
                                  : "#60a5fa"
                              }
                            />
                            <Text
                              className={`text-md font-kanit ${
                                item.ncr_type === "0"
                                  ? item.type === "good"
                                    ? "text-green-400"
                                    : item.type === "waste"
                                    ? "text-red-400"
                                    : "text-amber-400"
                                  : "text-blue-400"
                              }`}
                            >
                              {item.date ? item.date : t("no_date")}
                            </Text>
                          </View>
                        </View>

                        <View className="flex-row items-center">
                          <View
                            className={`flex-row items-center justify-center px-3 rounded-lg min-h-6 ${
                              item.ncr_type === "0"
                                ? item.type === "good"
                                  ? "bg-[#58d68d]"
                                  : item.type === "waste"
                                  ? "bg-[#ec7063]"
                                  : "bg-[#f4d03f]"
                                : "bg-[#60a5fa]"
                            }`}
                          >
                            {item.ncr_type === "0" ? (
                              item.type === "good" ? (
                                <Icon
                                  name="check-circle"
                                  size={15}
                                  className="pr-1"
                                  color="white"
                                />
                              ) : item.type === "waste" ? (
                                <Icon
                                  name="times-circle"
                                  size={15}
                                  className="pr-1"
                                  color="white"
                                />
                              ) : (
                                <Icon
                                  name="spinner"
                                  size={15}
                                  className="pr-1"
                                  color="white"
                                />
                              )
                            ) : (
                              <Icon
                                name="exclamation-circle"
                                size={15}
                                className="pr-1"
                                color="white"
                              />
                            )}
                            <Text className="text-white text-center font-kanit">
                              {item.ncr_type === "0"
                                ? item.type === "good"
                                  ? t("good")
                                  : item.type === "waste"
                                  ? t("waste")
                                  : "Wip"
                                : t("ncr_work")}
                            </Text>
                          </View>
                        </View>
                      </View>

                      <View
                        className={`flex flex-row items-center justify-between bg-white
                          border-b mb-3 pb-2 pt-1 border-gray-300 rounded-lg`}
                      >
                        {item.ncr_type === "0" ? (
                          item.type === "good" ? (
                            <View className="flex items-center justify-center rounded-full">
                              <Image
                                source={require("../../assets/cable_good.png")}
                                resizeMode="contain"
                                style={{ width: 48, height: 48 }}
                              />
                            </View>
                          ) : item.type === "waste" ? (
                            <View className="flex items-center justify-center rounded-full">
                              <Image
                                source={require("../../assets/cable_notgood.png")}
                                resizeMode="contain"
                                style={{ width: 48, height: 48 }}
                              />
                            </View>
                          ) : (
                            <View className="flex items-center justify-center rounded-full">
                              <Image
                                source={require("../../assets/cable_wip.png")}
                                resizeMode="contain"
                                style={{ width: 48, height: 48 }}
                              />
                            </View>
                          )
                        ) : (
                          <View className="flex items-center justify-center rounded-full">
                            <Image
                              source={require("../../assets/cable_ncr.png")}
                              resizeMode="contain"
                              style={{ width: 48, height: 48 }}
                            />
                          </View>
                        )}

                        <View className="flex-1 ml-3">
                          <View className="flex-row items-center">
                            <Text className="text-xl pt-1 font-kanit">
                              {item.order_name}
                            </Text>
                            {item.status === "1" ? (
                              <Text className="text-red-500 text-sm font-kanit ml-2 mt-2">
                                {t("urgent")}
                              </Text>
                            ) : null}
                          </View>
                          <Text className="text-gray-600 text-md font-kanit">
                            Order No. : {item.order_number}
                          </Text>
                        </View>
                      </View>

                      <View className="flex-row items-center justify-between">
                        <View className="flex-row items-center">
                          <Image
                            source={{
                              uri: `https://emp-image.veninecopper.com/${item.emp_code}.png`,
                            }}
                            resizeMode="contain"
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 24,
                              borderWidth: 1,
                              borderColor: "#e5e7eb",
                              marginRight: 8,
                            }}
                          />
                          <View>
                            <Text className="text-xs text-gray-600 font-kanit">
                              {item.emp_position}
                            </Text>
                            <Text className="text-base font-kanit">
                              {item.emp_name}
                            </Text>
                          </View>
                        </View>

                        <View
                          className={`flex-row items-center justify-center rounded-lg py-1.5 px-2 ${
                            item.ncr_type === "0"
                              ? item.type === "good"
                                ? "bg-green-50"
                                : item.type === "waste"
                                ? "bg-red-50"
                                : "bg-amber-50"
                              : "bg-blue-50"
                          }`}
                        >
                          <Icon
                            name="map-marker"
                            size={15}
                            className="pr-1"
                            color={
                              item.ncr_type === "0"
                                ? item.type === "good"
                                  ? "#58d68d"
                                  : item.type === "waste"
                                  ? "#ec7063"
                                  : "#f4d03f"
                                : "#60a5fa"
                            }
                          />
                          <Text
                            className={`text-md font-kanit ${
                              item.ncr_type === "0"
                                ? item.type === "good"
                                  ? "text-green-400"
                                  : item.type === "waste"
                                  ? "text-red-400"
                                  : "text-amber-400"
                                : "text-blue-400"
                            }`}
                          >
                            {t("position_order")}: {item.position}
                          </Text>
                        </View>
                      </View>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}
            </View>
          </>
        )}
      </View>
    </>
  );
}